import { IconHistory } from "../../../app/icons";
import { EmptyState, Loading } from "../../../app/Status";
import { useQuery } from "@tanstack/react-query";
import { compactHistory, historyLines, type HistoryEntryDto, type HistoryResponse } from "@klantpunt/shared";
import { api } from "../../../lib/api";
import { Drawer } from "./ui";

/**
 * Wijzigingsgeschiedenis van de planning (docs/ASSETS-MJOP.md, "Geschiedenis"): per regel in het
 * paneel, en voor het hele gebouw via *Wijzigingen*.
 */
export const historyKey = (buildingId: string, entity?: string) =>
  ["mjop", "history", buildingId, entity ?? "all"] as const;

function useHistory(buildingId: string, entity?: string) {
  return useQuery({
    queryKey: historyKey(buildingId, entity),
    queryFn: () => api<HistoryResponse>(`/buildings/${buildingId}/mjop-history${entity ? `?entity=${entity}` : ""}`),
  });
}

const when = (iso: string) =>
  new Date(iso).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function HistoryList({ entries, showLabel }: { entries: HistoryEntryDto[]; showLabel?: boolean }) {
  return (
    <ol className="pl-log">
      {entries.map((e) => (
        <li key={e.id}>
          <span className="pl-lw">
            {when(e.at)} · {e.by ?? "Onbekend"}
          </span>
          {showLabel && <strong className="pl-ll">{e.label}</strong>}
          {historyLines(e).map((l, i) => (
            <span key={i}>{l}</span>
          ))}
          {e.reason && <span className="pl-lwhy">{e.reason}</span>}
        </li>
      ))}
    </ol>
  );
}

/** Blok *Wijzigingen* in het paneel van één regel. */
export function HistorySection({ buildingId, entityId }: { buildingId: string; entityId: string }) {
  const q = useHistory(buildingId, entityId);
  const entries = compactHistory(q.data?.entries ?? []);
  return (
    <section className="pl-sect" aria-label="Wijzigingen">
      <h3>Wijzigingen</h3>
      {q.isPending ? (
        <Loading small />
      ) : entries.length ? (
        <HistoryList entries={entries} />
      ) : (
        <p className="muted small">Nog geen wijzigingen vastgelegd.</p>
      )}
    </section>
  );
}

/** Alle wijzigingen in de planning van een gebouw, nieuwste eerst. */
export function BuildingHistory({ buildingId, onClose }: { buildingId: string; onClose: () => void }) {
  const q = useHistory(buildingId);
  const entries = compactHistory(q.data?.entries ?? []);
  return (
    <Drawer
      eyebrow="Planning"
      title="Wijzigingen"
      sub={<span className="muted small">Laatste 100 wijzigingen in dit gebouw</span>}
      onClose={onClose}
      footer={
        <button type="button" className="btn primary" onClick={onClose}>
          Sluiten
        </button>
      }
    >
      {q.isPending ? (
        <Loading />
      ) : entries.length ? (
        <HistoryList entries={entries} showLabel />
      ) : (
        <EmptyState compact icon={IconHistory} title="Nog geen wijzigingen" />
      )}
    </Drawer>
  );
}
